import React, { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { CheckCircle, AlertCircle, Loader2, Mail, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getApiUrl } from "@/config";
import { extractSafeToken } from "@/utils/security";

export default function UnsubscribePage() {
  const [location, navigate] = useLocation();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState("");
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = extractSafeToken(urlParams, "token");
    setToken(tokenParam);

    if (!tokenParam) {
      setStatus("error");
      setMessage("Invalid or unsafe unsubscribe link. Please check your email and try again.");
      return;
    }
  }, []);

  const handleUnsubscribe = async () => {
    if (!token) return;

    setStatus("loading");

    try {
      const response = await fetch(getApiUrl("api/users/unsubscribe"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus("success");
        setMessage("You have been successfully unsubscribed from all Blockseer Explorer transaction alerts.");
      } else {
        setStatus("error");
        setMessage(data.message || "Failed to process unsubscribe request. The link may be invalid or expired.");
      }
    } catch (error) {
      console.error("Unsubscribe error:", error);
      setStatus("error");
      setMessage("An error occurred while processing your request. Please try again.");
    }
  };

  const renderContent = () => {
    if (status === "loading" && token) {
      return (
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-gray-900">
              Unsubscribe from Transaction Alerts
            </h1>
            <p className="text-gray-600 max-w-md mx-auto">
              Click the button below to unsubscribe from all Blockseer Explorer transaction alerts. 
              You can re-enable alerts anytime in your profile settings.
            </p>
          </div>

          <Button onClick={handleUnsubscribe} className="px-8">
            Unsubscribe from All Alerts
          </Button>
        </div>
      );
    }

    if (status === "loading") {
      return (
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />
            </div>
          </div>
          
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-gray-900">Processing...</h1>
            <p className="text-gray-600">Please wait while we process your unsubscribe request.</p>
          </div>
        </div>
      );
    }

    if (status === "success") {
      return (
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-green-900">
              Successfully Unsubscribed
            </h1>
            <p className="text-gray-600 max-w-md mx-auto">
              {message}
            </p>
          </div>

          <div className="space-y-3">
            <Button onClick={() => navigate("/profile")} variant="outline" className="w-full sm:w-auto">
              <Settings className="w-4 h-4 mr-2" />
              Manage Alert Settings
            </Button>
            <div>
              <Button onClick={() => navigate("/")} className="w-full sm:w-auto">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    if (status === "error") {
      return (
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          
          <div className="space-y-4">
            <h1 className="text-2xl font-bold text-red-900">
              Unsubscribe Failed
            </h1>
            <p className="text-gray-600 max-w-md mx-auto">
              {message}
            </p>
          </div>

          <div className="space-y-3">
            <Button onClick={() => navigate("/")} className="w-full sm:w-auto">
              Back to Home
            </Button>
            <div>
              <Button 
                onClick={() => navigate("/profile")} 
                variant="outline" 
                className="w-full sm:w-auto"
              >
                <Settings className="w-4 h-4 mr-2" />
                Manage Settings Manually
              </Button>
            </div>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-md p-8 border border-gray-200">
          {renderContent()}
        </div>
        
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500">
            Need help? Contact{" "}
            <a 
              href="mailto:support@terrapool.io" 
              className="text-blue-600 hover:text-blue-700 underline"
            >
              support@terrapool.io
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}